import FolderOutlined from "@mui/icons-material/FolderOutlined";
import ZoomInRounded from "@mui/icons-material/ZoomInRounded";
import { Box, Checkbox, Chip, IconButton, Stack, Typography } from "@mui/material";
import { useMemo, useState, type DragEvent, type FC, type MouseEvent } from "react";
import type { UUID } from "../../utils/uuid";
import { PaginationWrapper } from "../generic/components/pagination-wrapper";
import type { ViewMode } from "../generic/components/view-toggle";
import { AssetPreview } from "../message/editor/plugins/toolbar/content-library/asset-preview";
import { AssetCard } from "./asset-card";
import { FolderCard } from "./folder-card";
import { useDragState } from "./hooks/use-drag-state";
import type { Asset } from "./server/digital-asset-service";
import type { Folder } from "./server/folder-service";
import { getFolderName } from "./utils";

const AssetListRow: FC<{
  asset: Asset;
  selected: boolean;
  selectionMode: boolean;
  folderName: string | null;
  designer?: string | null;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  onDragStart?: (event: DragEvent) => void;
  onPreview?: () => void;
}> = ({
  asset,
  selected,
  selectionMode,
  folderName,
  designer,
  onClick,
  onDragStart,
  onPreview,
}) => {
  const [hover, setHover] = useState(false);
  const { isDragging, handlers } = useDragState<HTMLDivElement>({
    onDragStart: onDragStart as ((event: DragEvent<HTMLDivElement>) => void) | undefined,
  });

  return (
    <Box
      data-testid="asset"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      draggable={Boolean(onDragStart)}
      onDragStart={handlers.handleDragStart}
      onDragEnd={handlers.handleDragEnd}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        minHeight: 60,
        px: 2,
        py: 1,
        cursor: onDragStart ? (isDragging ? "grabbing" : "grab") : "pointer",
        opacity: isDragging ? 0.5 : 1,
        ...(selected
          ? {
              bgcolor: "action.selected",
              boxShadow: "inset 0 0 0 2px var(--mui-palette-primary-main)",
            }
          : {}),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
        {selectionMode && (
          <Checkbox
            checked={selected}
            slotProps={{ input: { "aria-label": `Select ${asset.title}` } }}
            sx={{ p: 0.5 }}
          />
        )}
        <Box
          sx={{
            width: 56,
            height: 40,
            flexShrink: 0,
            borderRadius: 0.75,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <AssetPreview
            asset={asset}
            height="100%"
            iconFontSize={24}
            showPreviewIcon={false}
            videoPlayer={false}
            designer={designer}
            showChips={false}
          />
        </Box>
        <Typography variant="body1" noWrap>
          {asset.title}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
        {hover && !selectionMode && onPreview && (
          <IconButton
            size="small"
            aria-label="Preview"
            data-analytics-id="content-library-list-preview-click"
            onClick={(e) => {
              e.stopPropagation();
              onPreview();
            }}
          >
            <ZoomInRounded fontSize="small" />
          </IconButton>
        )}
        {folderName ? (
          <Stack direction="row" spacing={0.5} alignItems="center" minWidth={0}>
            <FolderOutlined sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {folderName}
            </Typography>
          </Stack>
        ) : null}
        {asset.content_type ? <Chip size="small" label={asset.content_type} /> : null}
      </Stack>
    </Box>
  );
};

const FolderListRow: FC<{
  folder: Folder;
  onClick: () => void;
  onDragStart?: (event: DragEvent) => void;
  onDragOver?: (event: DragEvent) => void;
  onDrop?: (event: DragEvent) => void;
}> = ({ folder, onClick, onDragStart, onDragOver, onDrop }) => {
  const { isDragging, showDropHighlight, handlers } = useDragState<HTMLDivElement>({
    onDragStart: onDragStart as ((event: DragEvent<HTMLDivElement>) => void) | undefined,
    onDragOver: onDragOver as ((event: DragEvent<HTMLDivElement>) => void) | undefined,
    onDrop: onDrop as ((event: DragEvent<HTMLDivElement>) => void) | undefined,
  });

  return (
    <Box
      data-testid="folder-list-row"
      onClick={onClick}
      draggable={Boolean(onDragStart)}
      onDragStart={handlers.handleDragStart}
      onDragEnd={handlers.handleDragEnd}
      onDragOver={handlers.handleDragOver}
      onDragLeave={handlers.handleDragLeave}
      onDrop={handlers.handleDrop}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        minHeight: 60,
        px: 2,
        py: 1,
        gap: 2,
        cursor: onDragStart ? (isDragging ? "grabbing" : "grab") : "pointer",
        opacity: isDragging ? 0.5 : 1,
        ...(showDropHighlight
          ? {
              bgcolor: "action.hover",
              boxShadow: "inset 0 0 0 2px var(--mui-palette-primary-main)",
            }
          : {}),
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
        <FolderOutlined sx={{ color: "primary.main", fontSize: 22, flexShrink: 0 }} />
        <Typography variant="body1" noWrap>
          {folder.name}
        </Typography>
      </Stack>
      <Chip size="small" label="Folder" />
    </Box>
  );
};

export const AssetGrid: FC<{
  assets: Asset[];
  onClickFolder: (folderId: UUID) => void;
  designer?: string | null;
  onClick?: (asset: Asset, event: MouseEvent) => void;
  onPreview?: (asset: Asset) => void;
  selectedAsset?: Asset | null;
  selectedAssetIds?: UUID[];
  selectionMode?: boolean;
  folders?: Folder[];
  showFolderNames: boolean;
  showFolders?: boolean;
  onAssetDragStart?: (asset: Asset, event: DragEvent) => void;
  onFolderDragStart?: (folder: Folder, event: DragEvent) => void;
  onFolderDragOver?: (event: DragEvent) => void;
  onFolderDrop?: (folderId: Folder["id"], event: DragEvent) => void;
  allFolders?: Folder[];
  view?: ViewMode;
}> = ({
  assets,
  onClickFolder,
  designer,
  onClick,
  onPreview,
  selectedAsset,
  selectedAssetIds = [],
  selectionMode = false,
  folders,
  showFolderNames,
  showFolders = true,
  onAssetDragStart,
  onFolderDragStart,
  onFolderDragOver,
  onFolderDrop,
  allFolders,
  view = "grid",
}) => {
  const data = useMemo(
    () => (showFolders && folders ? [...folders, ...assets] : assets),
    [showFolders, folders, assets],
  );

  const isAsset = (item: unknown): item is Asset =>
    typeof item === "object" && item !== null && "folder_id" in item;

  const isFolder = (item: unknown): item is Folder =>
    typeof item === "object" && item !== null && "parent_folder_id" in item;

  return (
    <PaginationWrapper
      data={data}
      itemsPerPage={24}
      renderPage={(items) =>
        view === "list" ? (
          <Stack
            data-testid="content-library-asset-list-view"
            divider={<Box sx={{ borderBottom: "1px solid", borderColor: "divider" }} />}
          >
            {items.map((item) => {
              if (isAsset(item)) {
                return (
                  <AssetListRow
                    key={item.id}
                    asset={item}
                    selected={
                      selectionMode
                        ? selectedAssetIds.includes(item.id)
                        : selectedAsset?.id === item.id
                    }
                    selectionMode={selectionMode}
                    designer={designer}
                    onClick={(event) => onClick?.(item, event)}
                    folderName={showFolderNames ? getFolderName(item.folder_id, allFolders) : null}
                    onDragStart={
                      onAssetDragStart ? (event) => onAssetDragStart(item, event) : undefined
                    }
                    onPreview={onPreview ? () => onPreview(item) : undefined}
                  />
                );
              }

              if (isFolder(item)) {
                return (
                  <FolderListRow
                    key={item.id}
                    folder={item}
                    onClick={() => onClickFolder(item.id)}
                    onDragStart={
                      onFolderDragStart ? (event) => onFolderDragStart(item, event) : undefined
                    }
                    onDragOver={onFolderDragOver}
                    onDrop={onFolderDrop ? (event) => onFolderDrop(item.id, event) : undefined}
                  />
                );
              }

              return null;
            })}
          </Stack>
        ) : (
          <Box
            data-testid="content-library-asset-grid-view"
            display="grid"
            gap={2}
            gridTemplateColumns="repeat(auto-fit, minmax(200px, 305px))"
          >
            {items.map((item) => {
              if (isAsset(item)) {
                return (
                  <AssetCard
                    key={item.id}
                    asset={item}
                    selected={
                      selectionMode
                        ? selectedAssetIds.includes(item.id)
                        : selectedAsset?.id === item.id
                    }
                    selectionMode={selectionMode}
                    data-testid="asset"
                    onClick={(event) => onClick?.(item, event)}
                    designer={designer}
                    folderName={showFolderNames ? getFolderName(item.folder_id, allFolders) : null}
                    draggable={Boolean(onAssetDragStart)}
                    onDragStart={
                      onAssetDragStart ? (event) => onAssetDragStart(item, event) : undefined
                    }
                    onPreview={onPreview ? () => onPreview(item) : undefined}
                  />
                );
              }
              if (isFolder(item)) {
                return (
                  <FolderCard
                    key={item.id}
                    folder={item}
                    onClick={() => onClickFolder(item.id)}
                    draggable={Boolean(onFolderDragStart)}
                    onDragStart={
                      onFolderDragStart ? (event) => onFolderDragStart(item, event) : undefined
                    }
                    onDragOver={onFolderDragOver}
                    onDrop={onFolderDrop ? (event) => onFolderDrop(item.id, event) : undefined}
                  />
                );
              }

              return null;
            })}
          </Box>
        )
      }
    />
  );
};
